// Filtro por geocercas de usuario (attributes.devices_by_geofence).
//
// Port del isInGeofence legacy (Map.vue:3256) al pipeline de Map V2:
// guarda los polígonos que GET /geofences devuelve para el usuario de la
// sesión (el backend ya limita por el vínculo user↔geofence) y responde
// si un punto/entidad debe mostrarse. La decisión "qué hacer con el item"
// (descartar, estacionar, purgar) vive en MapV2.vue — este módulo solo
// conoce geometría y configuración.
//
// A diferencia del legacy, el universo incluye TAMBIÉN las geocercas de
// mando único (region/sector/cuadrante): el legacy deviceStore solo
// comparaba contra las regulares, lo que dejaba sin patrullas a los
// usuarios mando_unico + devices_by_geofence.
//
// Spec: docs/superpowers/specs/2026-07-20-map-v2-geofence-user-filter-design.md
import { wktToGeoJSON } from '../utils/wkt-parser.js'

// Tipos operativos que el filtro oculta. Exentos: presa/sismo/fire/cpr
// (contexto estatal) y derivados de selección (route_lpr,
// external_sensor_route) — su entidad padre ya está filtrada.
export const GEOFENCE_FILTERED_TYPES = {
  device: true,
  bus: true,
  camera: true,
  lpr: true,
  sensor: true,
  external_sensor: true,
  incident: true,
  sos_call: true,
  reportant: true,
  hit: true,
}

// Ray-casting clásico (mismo algoritmo que pointInEdomex en
// edomex-boundary.js): cuenta intersecciones del ray horizontal contra
// los segmentos del anillo. Impar = dentro.
function pointInRing(lng, lat, ring) {
  let inside = false
  const n = ring.length
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const xi = ring[i][0]
    const yi = ring[i][1]
    const xj = ring[j][0]
    const yj = ring[j][1]
    const intersect = ((yi > lat) !== (yj > lat))
      && (lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi)
    if (intersect) inside = !inside
  }
  return inside
}

class GeofenceMembership {
  constructor() {
    if (typeof GeofenceMembership.instace === 'object') {
      return GeofenceMembership.instace
    }
    this._enabled = false
    this._ready = false
    this._polygons = new Map() // id → { ring: [lng,lat][], bbox: {minLng,minLat,maxLng,maxLat} }
    this._polygonList = [] // snapshot de this._polygons.values() para contains() sin closures
    GeofenceMembership.instace = this
  }

  // Reconstruye el array plano usado por contains(). Se llama tras cada
  // mutación de this._polygons (setGeofences/removeGeofences/clear) para
  // que el hot path de lectura itere un array indexado en vez de un Map.
  _rebuildList() {
    this._polygonList = Array.from(this._polygons.values())
  }

  configure(enabled) {
    this._enabled = !!enabled
  }

  isEnabled() {
    return this._enabled
  }

  isReady() {
    return this._ready
  }

  isFilteredType(entityType) {
    return GEOFENCE_FILTERED_TYPES[entityType] === true
  }

  // Upsert de polígonos. Lista vacía también marca ready (fail-closed:
  // flag activo sin geocercas asignadas = no ve nada operativo).
  setGeofences(rawList) {
    (rawList || []).forEach(gf => {
      if (!gf || gf.id == null) return
      const geometry = wktToGeoJSON(gf.area)
      if (!geometry || !geometry.coordinates || !geometry.coordinates[0]) return
      // ring es la referencia cacheada de wktToGeoJSON (compartida con
      // geofence-renderer.js) — nunca mutar, solo leer.
      const ring = geometry.coordinates[0]
      if (ring.length < 3) return // polígono degenerado, no forma un área
      let minLng = Infinity
      let minLat = Infinity
      let maxLng = -Infinity
      let maxLat = -Infinity
      ring.forEach(point => {
        if (point[0] < minLng) minLng = point[0]
        if (point[1] < minLat) minLat = point[1]
        if (point[0] > maxLng) maxLng = point[0]
        if (point[1] > maxLat) maxLat = point[1]
      })
      const bbox = {
        minLng, minLat, maxLng, maxLat,
      }
      this._polygons.set(gf.id, { ring, bbox })
    })
    this._ready = true
    this._rebuildList()
  }

  // Reemplaza el universo completo. Lo usa la entrega inicial (caché o
  // GET /geofences), que trae TODAS las geocercas del usuario; setGeofences
  // es upsert y queda para los deltas del WS. Con upsert en la entrega
  // inicial, las geocercas cacheadas de otro usuario se sumaban a las propias.
  replaceGeofences(rawList) {
    this._polygons.clear()
    this.setGeofences(rawList)
  }

  removeGeofences(ids) {
    (ids || []).forEach(id => this._polygons.delete(id))
    this._rebuildList()
  }

  clear() {
    this._polygons.clear()
    this._polygonList = []
    this._ready = false
  }

  // true si el punto cae dentro de ALGÚN polígono del universo.
  // Bbox primero (4 comparaciones) — ray-casting solo en candidatos.
  // Hot path: se llama por-item en batches WS de hasta ~22K monitores,
  // así que itera un array plano con for indexado (sin closures) y
  // corta en el primer match — nada de Map#forEach aquí.
  contains(lng, lat) {
    const list = this._polygonList
    for (let i = 0; i < list.length; i++) {
      const b = list[i].bbox
      const insideBbox = lng >= b.minLng && lng <= b.maxLng && lat >= b.minLat && lat <= b.maxLat
      if (insideBbox && pointInRing(lng, lat, list[i].ring)) return true
    }
    return false
  }

  // Decisión central del filtro. coords = [lng, lat] | null.
  // Sin coords válidas → oculto (paridad legacy isInGeofence:3260).
  shouldShow(entityType, coords) {
    if (!this._enabled) return true
    if (!this.isFilteredType(entityType)) return true
    if (!coords || coords.length < 2 || coords[0] == null || coords[1] == null) return false
    return this.contains(coords[0], coords[1])
  }
}

export default new GeofenceMembership()
